import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Dialog, Label, Select, SelectItem } from '@utima/ui';
import { memo, useEffect, useMemo, useState } from 'react';

import { useFeedAttributeMutation } from './useFeedAttributeMutation';
import type { FeedAttributeRow } from './useFeedAttributesCols';

export type MergeAttributeDialogProps = {
  attribute: FeedAttributeRow | null;
  attributes: FeedAttributeRow[];
  onClose: () => void;
};

/**
 * Merging keeps the losing attribute rather than deleting it: its name and
 * aliases move onto the winner so the enrichment vocabulary still recognises
 * the old wording, and the row itself is switched off so nothing re-creates
 * it. That is the whole point — a deleted attribute comes back under the same
 * name on the next import.
 */
export const MergeAttributeDialog = memo(function MergeAttributeDialog({
  attribute,
  attributes,
  onClose,
}: MergeAttributeDialogProps) {
  const { t } = useLingui();
  const { merge } = useFeedAttributeMutation();

  const [targetId, setTargetId] = useState('');

  useEffect(() => {
    setTargetId('');
  }, [attribute]);

  const candidates = useMemo(
    () =>
      attributes.filter(
        candidate => candidate.active && candidate.id !== attribute?.id,
      ),
    [attributes, attribute],
  );

  const target = useMemo(
    () => candidates.find(candidate => candidate.id === targetId),
    [candidates, targetId],
  );

  /**
   * Values carry `numericValue` already converted into their own attribute's
   * `baseUnit`, and a merge re-points the rows without touching the numbers.
   * Merging a millilitre-based attribute into a litre-based one therefore
   * leaves 500 sitting next to 0.5 in the same range filter, with nothing in
   * the data to reveal it. Typing is not editable by contract, so this cannot
   * be reconciled automatically — the admin has to know, and then fix the
   * numbers on the affected products.
   */
  const sourceBaseUnit = attribute?.baseUnit ?? '';
  const targetBaseUnit = target?.baseUnit ?? '';
  const baseUnitsDisagree = Boolean(
    sourceBaseUnit && targetBaseUnit && sourceBaseUnit !== targetBaseUnit,
  );

  const handleSubmit = async () => {
    if (!attribute || !targetId) {
      return;
    }

    await merge.mutateAsync({ sourceId: attribute.id, targetId });
    onClose();
  };

  const sourceLabel = attribute?.displayName ?? attribute?.name ?? '';

  return (
    <Dialog.Root open={!!attribute} onOpenChange={onClose}>
      <Dialog.Content className='w-full max-w-[520px]'>
        <Dialog.Header>
          <Dialog.Title>
            <Trans>Merge attribute</Trans>
          </Dialog.Title>
          <Dialog.Description>
            {t`Values on ${sourceLabel} move to the attribute you pick, except on products that already have one. ${sourceLabel} keeps its row, is switched off for this feed, and its name is added to the winner's aliases so the vocabulary remembers it.`}
          </Dialog.Description>
        </Dialog.Header>

        <div className='flex flex-col gap-1.5 py-2'>
          <Label htmlFor='attribute-merge-target'>
            <Trans>Merge into</Trans>
          </Label>
          <Select
            id='attribute-merge-target'
            value={targetId}
            onValueChange={setTargetId}
            placeholder={t`Pick an attribute`}
          >
            {candidates.map(candidate => (
              <SelectItem key={candidate.id} value={candidate.id}>
                {candidate.displayName ?? candidate.name}
              </SelectItem>
            ))}
          </Select>
          {baseUnitsDisagree && (
            <span className='text-xs text-warning'>
              {t`These two are compared in different units (${sourceBaseUnit} and ${targetBaseUnit}). Merging moves the values as they are, so the numbers stop being comparable until you correct them on the affected products.`}
            </span>
          )}
          {candidates.length === 0 && (
            <span className='text-xs text-muted-fg'>
              <Trans>
                This feed has no other active attribute to merge into.
              </Trans>
            </span>
          )}
        </div>

        <Dialog.Footer>
          <Button variant='secondary' onClick={onClose}>
            <Trans>Cancel</Trans>
          </Button>
          <Button
            data-cta='admin-feed-attribute-merge-submit-button'
            variant='primary'
            loading={merge.isPending}
            disabled={!targetId}
            onClick={handleSubmit}
          >
            <Trans>Merge</Trans>
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
});
